import type { Course } from '../types/course';

/**
 * "Foundations to Fundamental Analysis": a Duolingo-style course
 * teaching beginners how to read and interpret financial data.
 *
 * Structure: 2 sections, 8 levels, 1 final quiz.
 * Every screen is interactive. Learn by doing, not reading.
 *
 * STYLE RULE: Do NOT use em dashes or dashes in any student-facing text.
 * Use commas, periods, semicolons, or "and" instead.
 */
export const FUNDAMENTALS_COURSE: Course = {
  id: 'fundamentals-v1',
  title: 'Foundations to Fundamental Analysis',
  description:
    'Learn how to read financial statements and key metrics, the same data used in every Wallstreetle puzzle.',
  sections: [
    // ═══════════════════════════════════════════════════════════
    // SECTION 1 - Financial Statements
    // ═══════════════════════════════════════════════════════════
    {
      id: 'financial-statements',
      title: 'Financial Statements',
      icon: '📄',
      levels: [
        // ─── Level 1 ──────────────────────────────────────────
        {
          id: 'level-1',
          title: 'What Is a Financial Statement?',
          subtitle: 'The report card every public company must share',
          screens: [
            {
              type: 'fill-in-the-blank',
              title: 'Companies Have Report Cards Too',
              icon: '📊',
              body: 'When a company sells shares on a stock exchange, it enters a deal with investors: in exchange for their money, it agrees to regularly disclose exactly how the business is doing. These detailed reports cover revenue, expenses, assets, and more, giving anyone the ability to evaluate the company\u2019s health.',
              template:
                'Every {{0}} company must publish {{1}}. Think of them as a report card that shows how the business is performing.',
              answers: ['public', 'financial statements'],
              distractors: ['private', 'tax returns', 'press releases'],
            },
            {
              type: 'match-pairs',
              title: 'The Big Three Statements',
              icon: '📋',
              body: 'There are three core financial statements every investor should know. Each one answers a different question about the company\u2019s financial health.',
              pairs: [
                ['Income Statement', 'How much money did the company make or lose?'],
                ['Balance Sheet', 'What does the company own vs. what does it owe?'],
                ['Cash Flow Statement', 'Where is the actual cash going?'],
              ],
            },
            {
              type: 'reveal-steps',
              title: 'Think of It Like a Household',
              icon: '🏠',
              body: 'Imagine you run a lemonade stand. Your income statement is your sales minus costs. Your balance sheet is what you own (pitcher, table) minus what you owe (loan from Mom). Your cash flow statement tracks the actual dollars coming in and going out of your piggy bank.',
              context:
                'Imagine you run a lemonade stand. Let\'s connect each statement to something familiar.',
              steps: [
                {
                  label: 'Income Statement',
                  detail:
                    'Your sales minus your costs. How much lemonade money did you actually keep?',
                },
                {
                  label: 'Balance Sheet',
                  detail:
                    'What you own (pitcher, table) minus what you owe (loan from Mom). Your net worth at a snapshot in time.',
                },
                {
                  label: 'Cash Flow Statement',
                  detail:
                    'The actual dollars coming in and going out of your piggy bank. Profit on paper doesn\'t mean cash in hand.',
                },
              ],
            },
            {
              type: 'quick-calc',
              title: 'Why Should You Care?',
              icon: '💡',
              body: 'Financial statements turn guesswork into informed decisions. Instead of "I think this company is doing well," you can say "Revenue grew 20% and profit margins expanded." That\'s exactly the kind of thinking Wallstreetle rewards.',
              scenario:
                'A company\'s revenue grew 20% and profit margins expanded. Instead of guessing, you can say "this company is performing well" based on data. What does the Income Statement tell you?',
              options: [
                'What the company owns',
                'How much money the company made or lost',
                'Where the cash is going',
                'How many shares exist',
              ],
              correctIndex: 1,
              explanation:
                'The Income Statement shows revenue, expenses, and resulting profit or loss. Financial statements turn guesswork into informed decisions.',
            },
            {
              type: 'fill-in-the-blank',
              title: 'Where to Find Them',
              icon: '🔍',
              body: 'The SEC requires companies to submit regular filings so investors always have up-to-date information. These filings happen on two schedules: one for each three-month period and another comprehensive annual report. You can look them up for free on EDGAR or sites like Yahoo Finance.',
              template:
                'Public companies file statements every quarter ({{0}}) and every year ({{1}}) with the SEC.',
              answers: ['10-Q', '10-K'],
              distractors: ['10-A', '10-R', '8-K'],
            },
            {
              type: 'tap-to-reveal-takeaways',
              title: 'Key Takeaways',
              icon: '🎯',
              bullets: [
                'Every public company publishes 3 core financial statements.',
                'Income Statement = performance, Balance Sheet = position, Cash Flow = cash movement.',
                'These are your best tools for understanding any company.',
              ],
            },
          ],
          quiz: [
            {
              id: 'l1-q1',
              question:
                'Which financial statement shows how much money a company made or lost?',
              options: [
                'Balance Sheet',
                'Income Statement',
                'Cash Flow Statement',
                'Annual Report',
              ],
              correctIndex: 1,
              explanation:
                'The Income Statement (also called the Profit & Loss statement) shows revenue, expenses, and resulting profit or loss over a period.',
            },
            {
              id: 'l1-q2',
              question:
                'If you wanted to know what a company owns vs. what it owes, which statement would you check?',
              options: [
                'Income Statement',
                'Cash Flow Statement',
                'Balance Sheet',
                'Earnings Report',
              ],
              correctIndex: 2,
              explanation:
                'The Balance Sheet lists a company\'s assets (what it owns), liabilities (what it owes), and shareholders\' equity at a specific point in time.',
            },
            {
              id: 'l1-q3',
              question:
                'How often do public companies file quarterly financial statements?',
              options: [
                'Monthly',
                'Every 3 months (10-Q)',
                'Twice a year',
                'Only annually',
              ],
              correctIndex: 1,
              explanation:
                'Public companies file a 10-Q every quarter and a 10-K annually with the SEC.',
            },
          ],
        },

        // ─── Level 2 ──────────────────────────────────────────
        {
          id: 'level-2',
          title: 'The Income Statement',
          subtitle: 'Revenue, expenses, and the bottom line',
          screens: [
            {
              type: 'fill-in-the-blank',
              title: 'The Company Scoreboard',
              icon: '🏆',
              body: 'The Income Statement answers one question: "Did this company make money?" It works top-down, starting with all the money that came in, then subtracting every cost along the way (salaries, materials, taxes) until you see what\u2019s actually left over. That final number is what investors care about most.',
              template:
                'The Income Statement starts with total {{0}} at the top and subtracts costs until you reach {{1}} at the bottom.',
              answers: ['revenue', 'net income'],
              distractors: ['assets', 'equity', 'cash flow'],
            },
            {
              type: 'sort-order',
              title: 'Key Line Items',
              icon: '📝',
              body: 'An income statement has several key line items, each building on the last. Revenue sits at the top, and after subtracting various costs you arrive at net income at the bottom.',
              prompt: 'Put these income statement items in order from top to bottom:',
              items: [
                'Revenue (Sales)',
                'Cost of Goods Sold (COGS)',
                'Gross Profit',
                'Operating Expenses',
                'Operating Income',
                'Net Income',
              ],
            },
            {
              type: 'reveal-steps',
              title: 'The Lemonade Stand Income Statement',
              icon: '🍋',
              body: 'You sold $100 of lemonade (Revenue). Lemons and sugar cost $30 (COGS), so Gross Profit is $70. You paid $20 for a sign and $10 to your friend (Operating Expenses). Operating Income is $40. After $5 in taxes, your Net Income is $35.',
              context:
                'Let\'s build an income statement for a lemonade stand, step by step.',
              steps: [
                {
                  label: 'Revenue',
                  detail: 'You sold $100 of lemonade.',
                  quickCheck: {
                    question: 'Your total sales are $100. This is your...?',
                    options: ['Net Income', 'Revenue', 'Gross Profit'],
                    correctIndex: 1,
                  },
                },
                {
                  label: 'COGS',
                  detail: 'Lemons and sugar cost $30.',
                },
                {
                  label: 'Gross Profit',
                  detail: '$100 - $30 = $70.',
                  quickCheck: {
                    question: 'What is $100 - $30?',
                    options: ['$60', '$70', '$80'],
                    correctIndex: 1,
                  },
                },
                {
                  label: 'Operating Expenses',
                  detail: 'Sign ($20) + friend\'s wages ($10) = $30.',
                },
                {
                  label: 'Operating Income',
                  detail: '$70 - $30 = $40.',
                  quickCheck: {
                    question: 'What is $70 - $30?',
                    options: ['$30', '$40', '$50'],
                    correctIndex: 1,
                  },
                },
                {
                  label: 'Net Income',
                  detail: 'After $5 in taxes: $40 - $5 = $35. That\'s your bottom line!',
                },
              ],
            },
            {
              type: 'classify',
              title: 'Top Line vs. Bottom Line',
              icon: '⚖️',
              body: 'Revenue is called the "top line" because it appears at the top of the income statement. Net income is the "bottom line," what\'s left after every cost is subtracted.',
              bucketA: 'Top Line (Revenue)',
              bucketB: 'Bottom Line (Net Income)',
              items: [
                { text: 'Total sales before any costs', bucket: 'A' },
                { text: 'What\'s left after all expenses', bucket: 'B' },
                { text: 'Shows market demand', bucket: 'A' },
                { text: 'Shows actual profitability', bucket: 'B' },
                { text: 'Can grow even if company loses money', bucket: 'A' },
                { text: 'Investors watch this closely', bucket: 'B' },
              ],
            },
            {
              type: 'quick-calc',
              title: 'Reading Between the Lines',
              icon: '🧠',
              body: 'A company with rising revenue but falling net income might be spending too much on growth. A company with flat revenue but rising net income is getting more efficient. The Income Statement tells a story, and your job is to read it.',
              scenario:
                'A company has rising revenue but falling net income. What might be happening?',
              options: [
                'The company is very profitable',
                'Costs are growing faster than revenue',
                'The company is paying fewer taxes',
                'Revenue doesn\'t affect net income',
              ],
              correctIndex: 1,
              explanation:
                'When expenses grow faster than revenue, profit margins shrink even though revenue is rising. A company with flat revenue but rising net income is getting more efficient.',
            },
            {
              type: 'tap-to-reveal-takeaways',
              title: 'Key Takeaways',
              icon: '🎯',
              bullets: [
                '"Top line" = Revenue. "Bottom line" = Net Income.',
                'Gross Profit shows how efficiently a company produces its goods.',
                'Growing revenue doesn\'t always mean growing profits.',
              ],
            },
          ],
          quiz: [
            {
              id: 'l2-q1',
              question: 'What is "Cost of Goods Sold" (COGS)?',
              options: [
                'The price customers pay',
                'The direct cost to produce goods or services',
                'Total operating expenses',
                'Taxes paid by the company',
              ],
              correctIndex: 1,
              explanation:
                'COGS represents the direct costs of producing goods (materials, manufacturing labor). Subtract COGS from revenue to get Gross Profit.',
            },
            {
              id: 'l2-q2',
              question:
                'A company has $500M revenue and $350M in total expenses. What is its net income?',
              options: ['$500M', '$350M', '$150M', '$850M'],
              correctIndex: 2,
              explanation:
                'Net Income = Revenue ($500M) minus Total Expenses ($350M) = $150M.',
            },
            {
              id: 'l2-q3',
              question:
                'Revenue is growing but net income is shrinking. What might be happening?',
              options: [
                'The company is very profitable',
                'Costs are growing faster than revenue',
                'The company is paying fewer taxes',
                'Revenue doesn\'t affect net income',
              ],
              correctIndex: 1,
              explanation:
                'When expenses grow faster than revenue, profit margins shrink even though revenue is rising. This can signal over-spending or margin pressure.',
            },
            {
              id: 'l2-q4',
              question:
                'Which term refers to Revenue minus Cost of Goods Sold?',
              options: ['Net Income', 'Operating Income', 'Gross Profit', 'EBITDA'],
              correctIndex: 2,
              explanation:
                'Gross Profit = Revenue - COGS. It shows how much money is left after covering direct production costs.',
            },
          ],
        },

        // ─── Level 3 ──────────────────────────────────────────
        {
          id: 'level-3',
          title: 'Balance Sheet & Cash Flow',
          subtitle: 'What a company owns, owes, and where cash goes',
          screens: [
            {
              type: 'fill-in-the-blank',
              title: 'A Snapshot in Time',
              icon: '📸',
              body: 'Unlike the Income Statement which covers a period of time, the Balance Sheet freezes the clock. It captures a company\u2019s financial position at one specific date, listing its assets on one side and its debts and shareholder claims on the other.',
              template:
                'The Balance Sheet is a snapshot at a single moment: "Right now, what does this company {{0}}, {{1}}, and what\'s left for shareholders?"',
              answers: ['own', 'owe'],
              distractors: ['earn', 'spend', 'invest'],
            },
            {
              type: 'fill-in-the-blank',
              title: 'The Balance Sheet Equation',
              icon: '⚖️',
              body: 'There\u2019s a fundamental equation at the heart of every balance sheet. On one side, you have everything the company possesses, including cash, buildings, and patents. On the other, you have the two groups with claims on those things: creditors (who lent money) and owners (who invested). The two sides must always balance.',
              template: '{{0}} = {{1}} + Shareholders\' Equity',
              answers: ['Assets', 'Liabilities'],
              distractors: ['Revenue', 'Net Income', 'Cash Flow'],
            },
            {
              type: 'reveal-steps',
              title: 'Your Personal Balance Sheet',
              icon: '🏡',
              body: 'Imagine your home is worth $300,000 (asset). You still owe $200,000 on the mortgage (liability). Your equity is $100,000. That\'s what you\'d pocket if you sold the house and paid off the loan. Companies work the same way, just with more zeroes.',
              context:
                'Let\'s build a personal balance sheet to understand the concept.',
              steps: [
                {
                  label: 'Asset: Your Home',
                  detail: 'Your home is worth $300,000.',
                  quickCheck: {
                    question: 'What is the asset in this example?',
                    options: ['The mortgage', 'The home ($300,000)', 'Your equity'],
                    correctIndex: 1,
                  },
                },
                {
                  label: 'Liability: The Mortgage',
                  detail: 'You still owe $200,000 on the mortgage.',
                },
                {
                  label: 'Equity: What\'s Yours',
                  detail:
                    '$300,000 - $200,000 = $100,000. That\'s what you\'d pocket if you sold and paid off the loan.',
                  quickCheck: {
                    question: 'What is $300,000 - $200,000?',
                    options: ['$50,000', '$100,000', '$200,000'],
                    correctIndex: 1,
                  },
                },
              ],
            },
            {
              type: 'quick-calc',
              title: 'Enter: Cash Flow',
              icon: '💰',
              body: 'Profit on paper doesn\'t always mean cash in the bank. The Cash Flow Statement tracks actual cash movement. A profitable company can still run out of cash if customers don\'t pay on time or if it\'s spending heavily on new factories.',
              scenario:
                'A company reports $50M net income (profit on paper) but only $10M in actual cash from operations. Can this company still run out of cash?',
              options: [
                'No, profit guarantees cash',
                'Yes, profit on paper doesn\'t always mean cash in the bank',
                'Only if it has no assets',
                'Cash flow and profit are the same thing',
              ],
              correctIndex: 1,
              explanation:
                'A profitable company can still run out of cash if customers don\'t pay on time or if it\'s spending heavily on new factories. The Cash Flow Statement tracks actual cash movement.',
            },
            {
              type: 'match-pairs',
              title: 'Three Types of Cash Flow',
              icon: '🔄',
              body: 'The Cash Flow Statement breaks cash movement into three categories. Each tells you something different about where the company\'s money is going.',
              pairs: [
                ['Operating', 'Cash from core business activities'],
                ['Investing', 'Cash for long-term assets (equipment, acquisitions)'],
                ['Financing', 'Cash from/to investors and lenders'],
              ],
            },
            {
              type: 'classify',
              title: 'How the 3 Statements Connect',
              icon: '🔗',
              body: 'The three financial statements aren\'t independent; they\'re deeply connected. Net income from the Income Statement flows into shareholders\' equity on the Balance Sheet, and the Cash Flow Statement reconciles profit with actual cash.',
              bucketA: 'Income Statement',
              bucketB: 'Balance Sheet',
              items: [
                { text: 'Net income flows into equity', bucket: 'A' },
                { text: 'Equity updated by net income', bucket: 'B' },
                { text: 'Shows performance over time', bucket: 'A' },
                { text: 'Snapshot at a point in time', bucket: 'B' },
                { text: 'Revenue & expenses', bucket: 'A' },
                { text: 'Assets, liabilities & equity', bucket: 'B' },
              ],
            },
            {
              type: 'tap-to-reveal-takeaways',
              title: 'Key Takeaways',
              icon: '🎯',
              bullets: [
                'Assets = Liabilities + Equity. The balance sheet must always balance.',
                'Cash flow ≠ profit. A company can be profitable on paper but cash-poor.',
                'Operating cash flow is the most important because it shows the health of the core business.',
                'All three statements are connected: net income feeds into equity and cash flow.',
              ],
            },
          ],
          quiz: [
            {
              id: 'l3-q1',
              question: 'What is the fundamental balance sheet equation?',
              options: [
                'Revenue - Expenses = Profit',
                'Assets = Liabilities + Equity',
                'Cash In - Cash Out = Net Cash',
                'Assets - Equity = Liabilities',
              ],
              correctIndex: 1,
              explanation:
                'The balance sheet equation is Assets = Liabilities + Shareholders\' Equity. It must always balance.',
            },
            {
              id: 'l3-q2',
              question:
                'A company reports strong profits but negative operating cash flow. What could this indicate?',
              options: [
                'The company is very healthy',
                'Customers aren\'t paying on time or expenses are cash-heavy',
                'The income statement is wrong',
                'It has no liabilities',
              ],
              correctIndex: 1,
              explanation:
                'Negative operating cash flow despite profits can mean customers are slow to pay (high receivables) or the company has large non-cash revenue. This is a yellow flag.',
            },
            {
              id: 'l3-q3',
              question:
                'Which type of cash flow covers buying new factories or equipment?',
              options: ['Operating', 'Investing', 'Financing', 'Capital'],
              correctIndex: 1,
              explanation:
                'Investing cash flow covers purchases and sales of long-term assets like property, equipment, and acquisitions.',
            },
            {
              id: 'l3-q4',
              question: 'What does shareholders\' equity represent?',
              options: [
                'Total company debt',
                'Cash in the bank',
                'What\'s left for shareholders after subtracting liabilities from assets',
                'Annual revenue',
              ],
              correctIndex: 2,
              explanation:
                'Equity = Assets - Liabilities. It represents the residual value that belongs to shareholders.',
            },
          ],
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════
    // SECTION 2 - Financial Metrics
    // ═══════════════════════════════════════════════════════════
    {
      id: 'financial-metrics',
      title: 'Financial Metrics',
      icon: '📈',
      levels: [
        // ─── Level 4 ──────────────────────────────────────────
        {
          id: 'level-4',
          title: 'Market Cap & Valuation',
          subtitle: 'How to tell if a stock is cheap or expensive',
          screens: [
            {
              type: 'fill-in-the-blank',
              title: 'What Is Market Cap?',
              icon: '🏷️',
              body: 'Market capitalization tells you what the entire stock market thinks a company is worth right now. It\u2019s a simple multiplication: take what one share costs and scale it up by every share that exists. For example, a $100 stock with 1 billion shares means investors collectively value the company at $100 billion.',
              template:
                'Market capitalization is calculated by multiplying the {{0}} by the total number of {{1}}.',
              answers: ['share price', 'shares outstanding'],
              distractors: ['net income', 'P/E ratio', 'revenue'],
            },
            {
              type: 'sort-order',
              title: 'Market Cap Buckets',
              icon: '📊',
              body: 'Companies are grouped by their market cap into size categories. Larger companies tend to be more stable, while smaller ones can grow faster but carry more risk.',
              prompt: 'Order these market cap buckets from largest to smallest:',
              items: [
                'Mega-cap: $200B+',
                'Large-cap: $10B to $200B',
                'Mid-cap: $2B to $10B',
                'Small-cap: $300M to $2B',
                'Micro-cap: Under $300M',
              ],
            },
            {
              type: 'quick-calc',
              title: 'The P/E Ratio',
              icon: '🔢',
              body: 'The Price-to-Earnings (P/E) ratio asks: "How many years of current earnings would it take to pay back the stock price?" Higher P/E can mean investors expect faster growth, or that the stock is overpriced.',
              scenario:
                'A stock trades at $60 with earnings per share (EPS) of $3. What is its P/E ratio?',
              formula: 'P/E = Price ÷ EPS',
              options: ['3', '20', '57', '180'],
              correctIndex: 1,
              explanation:
                'P/E = $60 ÷ $3 = 20. You\'re paying $20 for every $1 of annual earnings. Higher P/E can mean investors expect faster growth, or that the stock is overpriced.',
            },
            {
              type: 'quick-calc',
              title: 'The P/S Ratio',
              icon: '💲',
              body: 'The Price-to-Sales (P/S) ratio compares stock price to revenue. It\'s useful for companies that aren\'t profitable yet. If there\'s no earnings, you can\'t calculate a P/E.',
              scenario:
                'A company has a market cap of $500M and annual revenue of $100M. What is its P/S ratio?',
              formula: 'P/S = Market Cap ÷ Revenue',
              options: ['0.2', '5', '50', '600'],
              correctIndex: 1,
              explanation:
                'P/S = $500M ÷ $100M = 5. The market values the company at 5× its annual revenue. P/S is useful for companies that aren\'t profitable yet.',
            },
            {
              type: 'classify',
              title: 'P/E vs. P/S',
              icon: '⚖️',
              body: 'Both P/E and P/S help you judge whether a stock is cheap or expensive, but they work best in different situations. Understanding when to use each one is key to making better comparisons.',
              bucketA: 'P/E Ratio',
              bucketB: 'P/S Ratio',
              items: [
                { text: 'Price ÷ Earnings per Share', bucket: 'A' },
                { text: 'Price ÷ Sales per Share', bucket: 'B' },
                { text: 'Only works for profitable companies', bucket: 'A' },
                { text: 'Works for unprofitable companies too', bucket: 'B' },
                { text: 'Most common valuation metric', bucket: 'A' },
                { text: 'Useful for high-growth startups', bucket: 'B' },
              ],
            },
            {
              type: 'tap-to-reveal-takeaways',
              title: 'Key Takeaways',
              icon: '🎯',
              bullets: [
                'Market Cap = Share Price × Shares Outstanding.',
                'P/E tells you how much you\'re paying per dollar of earnings.',
                'P/S is useful when a company isn\'t profitable yet.',
                'A "high" or "low" P/E depends on the industry and growth expectations.',
              ],
            },
          ],
          quiz: [
            {
              id: 'l4-q1',
              question:
                'Company A has a stock price of $50 and 2 billion shares. What is its market cap?',
              options: ['$25 billion', '$50 billion', '$100 billion', '$150 billion'],
              correctIndex: 2,
              explanation: 'Market Cap = $50 × 2 billion shares = $100 billion.',
            },
            {
              id: 'l4-q2',
              question:
                'A stock trades at $60 with earnings per share of $3. What is its P/E ratio?',
              options: ['3', '20', '60', '180'],
              correctIndex: 1,
              explanation:
                'P/E = Price ($60) ÷ EPS ($3) = 20. You\'re paying $20 for every $1 of earnings.',
            },
            {
              id: 'l4-q3',
              question:
                'Why might investors use the P/S ratio instead of P/E?',
              options: [
                'P/S is always more accurate',
                'The company isn\'t profitable yet, so there are no earnings',
                'P/E only works for tech companies',
                'P/S measures debt levels',
              ],
              correctIndex: 1,
              explanation:
                'When a company has no earnings (negative net income), you can\'t compute a meaningful P/E. P/S uses revenue instead.',
            },
          ],
        },

        // ─── Level 5 ──────────────────────────────────────────
        {
          id: 'level-5',
          title: 'Profitability Metrics',
          subtitle: 'Is this company actually making money?',
          screens: [
            {
              type: 'quick-calc',
              title: 'Revenue Isn\'t Profit',
              icon: '💸',
              body: 'A company can have billions in revenue and still lose money. Profitability metrics cut through the noise by expressing profit as a percentage of revenue, so you can compare companies of very different sizes.',
              scenario:
                'Company A has $10B in revenue but $11B in total expenses. Is it profitable?',
              options: [
                'Yes, $10B in revenue is huge',
                'No, expenses exceed revenue by $1B',
                'Can\'t tell without the balance sheet',
                'Only if its P/E is positive',
              ],
              correctIndex: 1,
              explanation:
                'A company can have billions in revenue and still lose money. Profitability metrics cut through the noise by expressing profit as a percentage of revenue.',
            },
            {
              type: 'sort-order',
              title: 'The Margin Family',
              icon: '📐',
              body: 'There are three key margins, each measuring profitability at a different level. Gross Margin strips away production costs, Operating Margin adds overhead, and Net Margin includes everything.',
              prompt:
                'Order these margins from broadest (most costs included) to narrowest (fewest costs subtracted):',
              items: [
                'Gross Margin (Revenue minus COGS)',
                'Operating Margin (minus operating expenses too)',
                'Net Margin (minus everything including taxes)',
              ],
            },
            {
              type: 'reveal-steps',
              title: 'Margins at the Lemonade Stand',
              icon: '🍋',
              body: 'If you sell $100 of lemonade and lemons cost $30, your Gross Margin is 70%. After paying rent and your friend ($20 + $10), Operating Margin is 40%. After taxes ($5), Net Margin is 35%. Each margin peels back another layer of costs.',
              context:
                'You sold $100 of lemonade. Let\'s compute each margin step by step.',
              steps: [
                {
                  label: 'Gross Margin',
                  detail:
                    'Revenue $100 minus COGS $30 = $70 Gross Profit. Gross Margin = 70%.',
                  quickCheck: {
                    question: 'What is $70 ÷ $100 as a percentage?',
                    options: ['30%', '50%', '70%'],
                    correctIndex: 2,
                  },
                },
                {
                  label: 'Operating Margin',
                  detail:
                    'Gross Profit $70 minus OpEx $30 = $40 Operating Income. Operating Margin = 40%.',
                  quickCheck: {
                    question: 'What is $40 ÷ $100 as a percentage?',
                    options: ['30%', '40%', '70%'],
                    correctIndex: 1,
                  },
                },
                {
                  label: 'Net Margin',
                  detail:
                    'Operating Income $40 minus Taxes $5 = $35 Net Income. Net Margin = 35%.',
                  quickCheck: {
                    question: 'What is $35 ÷ $100 as a percentage?',
                    options: ['25%', '35%', '45%'],
                    correctIndex: 1,
                  },
                },
              ],
            },
            {
              type: 'quick-calc',
              title: 'What\'s a "Good" Margin?',
              icon: '🤔',
              body: 'What counts as a "good" margin depends entirely on the industry. Software companies often have 70-80% gross margins because code is cheap to copy. Grocery stores might have 25-30% because food is expensive to source. Always compare within the same industry.',
              scenario:
                'Software companies often have 70-80% gross margins because code is cheap to copy. Grocery stores might have 25-30%. A software company reports 28% gross margin. Is this concerning?',
              options: [
                'No, 28% is fine for any industry',
                'Yes, it\'s well below the software industry norm of 70-80%',
                'Can\'t tell without revenue numbers',
                'Gross margin doesn\'t matter for software',
              ],
              correctIndex: 1,
              explanation:
                'Always compare margins within the same industry. A 28% gross margin is great for groceries but a major red flag for a software company.',
            },
            {
              type: 'classify',
              title: 'High vs. Low Margin Businesses',
              icon: '⚖️',
              body: 'Different business models produce very different margin profiles. Understanding these patterns helps you spot what industry a mystery company operates in, a key skill for Wallstreetle.',
              bucketA: 'High Margin (Software)',
              bucketB: 'Low Margin (Retail)',
              items: [
                { text: 'Gross margin: 70-85%', bucket: 'A' },
                { text: 'Gross margin: 25-40%', bucket: 'B' },
                { text: 'Low COGS (digital product)', bucket: 'A' },
                { text: 'High COGS (physical goods)', bucket: 'B' },
                { text: 'Scales easily', bucket: 'A' },
                { text: 'Needs high volume to profit', bucket: 'B' },
              ],
            },
            {
              type: 'tap-to-reveal-takeaways',
              title: 'Key Takeaways',
              icon: '🎯',
              bullets: [
                'Margins express profit as a percentage, making companies comparable regardless of size.',
                'Gross → Operating → Net: each margin strips away more costs.',
                'Always compare margins within the same industry.',
              ],
            },
          ],
          quiz: [
            {
              id: 'l5-q1',
              question:
                'A company has $200M in revenue and $60M in net income. What is its net margin?',
              options: ['20%', '30%', '60%', '200%'],
              correctIndex: 1,
              explanation:
                'Net Margin = Net Income ($60M) ÷ Revenue ($200M) = 30%.',
            },
            {
              id: 'l5-q2',
              question:
                'Which margin tells you how efficiently a company produces its goods before any other expenses?',
              options: [
                'Net Margin',
                'Operating Margin',
                'Gross Margin',
                'Profit Margin',
              ],
              correctIndex: 2,
              explanation:
                'Gross Margin = (Revenue - COGS) ÷ Revenue. It isolates production efficiency before accounting for operating costs, interest, or taxes.',
            },
            {
              id: 'l5-q3',
              question:
                'A software company and a grocery chain both have $1B in revenue. Which likely has a higher gross margin?',
              options: [
                'The grocery chain',
                'The software company',
                'They\'d be the same',
                'Can\'t tell from revenue alone',
              ],
              correctIndex: 1,
              explanation:
                'Software has very low COGS (code costs almost nothing to reproduce), typically giving 70-85% gross margins vs. 25-35% for groceries.',
            },
          ],
        },

        // ─── Level 6 ──────────────────────────────────────────
        {
          id: 'level-6',
          title: 'Growth Metrics',
          subtitle: 'Is this company getting bigger?',
          screens: [
            {
              type: 'quick-calc',
              title: 'Growth Is the Name of the Game',
              icon: '🚀',
              body: 'Investors pay a premium for growth. A company growing revenue at 30% per year will likely be worth much more in the future than one growing at 3%. Growth metrics help you spot companies on the rise and spot when growth is slowing.',
              scenario:
                'Company A grows revenue at 30% per year. Company B grows at 3% per year. After 5 years, which will likely be worth more (assuming similar starting points)?',
              options: [
                'Company B, slow and steady wins',
                'Company A, high growth compounds dramatically',
                'They\'ll be about equal',
                'Can\'t tell without profit data',
              ],
              correctIndex: 1,
              explanation:
                'Investors pay a premium for growth. A company growing at 30% per year will roughly quadruple in 5 years, while 3% growth barely moves the needle.',
            },
            {
              type: 'match-pairs',
              title: 'Key Growth Metrics',
              icon: '📊',
              body: 'Growth can be measured in several ways. Revenue growth tells you if the company is selling more, while EPS growth tells you if profitability per share is improving.',
              pairs: [
                ['Revenue Growth (YoY)', 'Percentage increase in revenue vs. same period last year'],
                ['EPS Growth', 'Percentage increase in Earnings Per Share'],
                ['Quarterly vs. Annual', 'Quarterly is fresher; annual smooths out seasonality'],
              ],
            },
            {
              type: 'quick-calc',
              title: 'Revenue Growth vs. EPS Growth',
              icon: '🔍',
              body: 'A company can grow revenue by spending more (hiring, marketing) but that doesn\'t guarantee more profit per share. EPS growth combines top-line growth with cost discipline. The best companies grow both.',
              scenario:
                'A company had $80M revenue last year and $100M this year. What is its year-over-year revenue growth?',
              formula: 'Growth = (New - Old) ÷ Old',
              options: ['20%', '25%', '80%', '100%'],
              correctIndex: 1,
              explanation:
                'Growth = ($100M - $80M) ÷ $80M = 25%. Revenue growth doesn\'t guarantee EPS growth. The company may be spending more to achieve it.',
            },
            {
              type: 'reveal-steps',
              title: 'The Expanding Lemonade Empire',
              icon: '🍋',
              body: 'Last summer you made $100. This summer you made $130. That\'s 30% revenue growth. But if you hired two extra friends and your profit per stand actually dropped, your EPS growth might be negative, even though you\'re selling more lemonade.',
              context:
                'Your lemonade stand is growing! But is all growth created equal?',
              steps: [
                {
                  label: 'Last Summer: $100 Revenue',
                  detail: 'You ran one stand and made $100 in sales.',
                },
                {
                  label: 'This Summer: $130 Revenue',
                  detail: 'You opened a second stand. Revenue grew 30%!',
                  quickCheck: {
                    question: 'What is ($130 - $100) ÷ $100?',
                    options: ['13%', '23%', '30%'],
                    correctIndex: 2,
                  },
                },
                {
                  label: 'But... EPS Dropped',
                  detail:
                    'You hired two extra friends. Profit per stand actually fell. Revenue grew but EPS growth was negative.',
                },
              ],
            },
            {
              type: 'classify',
              title: 'Growth Traps',
              icon: '⚠️',
              body: 'Be wary of companies growing revenue through acquisitions (buying other companies) rather than organic growth. Also watch out for decelerating growth. Going from 40% to 30% to 20% can tank a stock price even though revenue is still increasing.',
              bucketA: 'Healthy Growth',
              bucketB: 'Growth Trap',
              items: [
                { text: 'Revenue growing from more customers', bucket: 'A' },
                { text: 'Revenue growing only through acquisitions', bucket: 'B' },
                { text: 'Both revenue and EPS increasing', bucket: 'A' },
                { text: 'Growth rate: 40% → 30% → 20% (decelerating)', bucket: 'B' },
                { text: 'Organic market expansion', bucket: 'A' },
                { text: 'Revenue up but margins shrinking', bucket: 'B' },
              ],
            },
            {
              type: 'tap-to-reveal-takeaways',
              title: 'Key Takeaways',
              icon: '🎯',
              bullets: [
                'Revenue growth shows if demand is increasing.',
                'EPS growth shows if profitability per share is increasing.',
                'Decelerating growth can be as damaging as no growth at all.',
              ],
            },
          ],
          quiz: [
            {
              id: 'l6-q1',
              question:
                'A company had $80M revenue last year and $100M this year. What is its YoY revenue growth?',
              options: ['20%', '25%', '80%', '100%'],
              correctIndex: 1,
              explanation:
                'Growth = (New - Old) ÷ Old = ($100M - $80M) ÷ $80M = 25%.',
            },
            {
              id: 'l6-q2',
              question:
                'Why might revenue growth be positive while EPS growth is negative?',
              options: [
                'That\'s impossible',
                'The company is spending more to grow, squeezing per-share profit',
                'EPS doesn\'t relate to revenue',
                'Revenue and EPS always move together',
              ],
              correctIndex: 1,
              explanation:
                'If a company grows revenue by spending aggressively (hiring, marketing, acquisitions), costs may outpace revenue gains, reducing profit per share.',
            },
            {
              id: 'l6-q3',
              question:
                'A company\'s revenue growth went from 50% to 35% to 20% over three years. What is this called?',
              options: [
                'Revenue decline',
                'Decelerating growth',
                'Negative earnings',
                'Margin compression',
              ],
              correctIndex: 1,
              explanation:
                'Revenue is still growing but at a slower rate. This is decelerating growth. Markets often react negatively because future expectations shrink.',
            },
          ],
        },

        // ─── Level 7 ──────────────────────────────────────────
        {
          id: 'level-7',
          title: 'Financial Health',
          subtitle: 'Can this company survive tough times?',
          screens: [
            {
              type: 'fill-in-the-blank',
              title: 'Profits Don\'t Matter if You\'re Broke',
              icon: '🏥',
              body: 'Earnings on an income statement don\u2019t guarantee survival. Plenty of companies have shown positive net income while running out of cash to cover day-to-day obligations: rent, payroll, supplier invoices. Financial health metrics exist to catch this mismatch before it becomes a crisis.',
              template:
                'A company can be {{0}} on paper but collapse if it can\'t pay its {{1}}.',
              answers: ['profitable', 'bills'],
              distractors: ['growing', 'shareholders', 'dividends'],
            },
            {
              type: 'match-pairs',
              title: 'Key Health Metrics',
              icon: '🩺',
              body: 'Financial health is measured by a handful of key ratios that show how well a company can handle its debts and short-term obligations. These metrics are your early warning system for trouble.',
              pairs: [
                ['Debt-to-Equity', 'Total debt ÷ shareholders\' equity'],
                ['Current Ratio', 'Current assets ÷ current liabilities'],
                ['Cash Position', 'Raw cash and equivalents on hand'],
                ['Free Cash Flow', 'Operating cash flow minus capital expenditures'],
              ],
            },
            {
              type: 'reveal-steps',
              title: 'Financial Health Check',
              icon: '🏠',
              body: 'Think of debt-to-equity like a personal debt-to-income ratio. If you earn $5,000/month and owe $2,500 in payments, that\'s tight. If you only owe $500, you have breathing room. Companies work the same way. Too much debt relative to equity means higher risk.',
              context:
                'Think of debt-to-equity like a personal debt-to-income ratio.',
              steps: [
                {
                  label: 'Comfortable Debt',
                  detail:
                    'You earn $5,000/month and owe $500 in payments. Plenty of breathing room.',
                },
                {
                  label: 'Tight Debt',
                  detail:
                    'You earn $5,000/month and owe $2,500 in payments. That\'s very tight.',
                  quickCheck: {
                    question: 'Which is riskier: D/E of 0.3 or D/E of 3.0?',
                    options: ['0.3', '3.0', 'They\'re equal'],
                    correctIndex: 1,
                  },
                },
                {
                  label: 'Companies Work the Same Way',
                  detail:
                    'Too much debt relative to equity means higher risk. But some debt is strategic if borrowed at low rates to fund high-return growth.',
                },
              ],
            },
            {
              type: 'classify',
              title: 'Healthy vs. Risky Balance Sheet',
              icon: '⚖️',
              body: 'You can quickly gauge a company\'s financial health by looking at a few key numbers. Companies with low debt, strong cash flow, and the ability to cover their short-term bills are in a much safer position.',
              bucketA: 'Healthy Company',
              bucketB: 'Risky Company',
              items: [
                { text: 'Debt-to-Equity: 0.3', bucket: 'A' },
                { text: 'Debt-to-Equity: 3.0', bucket: 'B' },
                { text: 'Current Ratio: 2.5', bucket: 'A' },
                { text: 'Current Ratio: 0.7', bucket: 'B' },
                { text: 'Strong free cash flow', bucket: 'A' },
                { text: 'Negative free cash flow', bucket: 'B' },
              ],
            },
            {
              type: 'quick-calc',
              title: 'Why Debt Isn\'t Always Bad',
              icon: '💭',
              body: 'Some debt is strategic. A company might borrow at low interest rates to fund growth that earns a much higher return. The key is whether debt is manageable relative to earnings and cash flow. It\'s all about balance.',
              scenario:
                'A company borrows $100M at 4% interest to fund a project that returns 15%. Is this smart use of debt?',
              options: [
                'No, all debt is bad',
                'Yes, the return (15%) exceeds the cost of borrowing (4%)',
                'Only if D/E is below 1.0',
                'Debt never affects returns',
              ],
              correctIndex: 1,
              explanation:
                'Some debt is strategic. The key is whether debt is manageable relative to earnings and cash flow, and whether the return exceeds the cost of borrowing.',
            },
            {
              type: 'tap-to-reveal-takeaways',
              title: 'Key Takeaways',
              icon: '🎯',
              bullets: [
                'Debt-to-Equity shows how leveraged a company is. Lower = safer, generally.',
                'Current Ratio above 1.0 means the company can cover short-term obligations.',
                'Free Cash Flow is the ultimate health metric: actual cash available after reinvesting.',
                'Some debt is fine; the question is whether it\'s manageable.',
              ],
            },
          ],
          quiz: [
            {
              id: 'l7-q1',
              question:
                'A company has $500M in total debt and $250M in shareholders\' equity. What is its debt-to-equity ratio?',
              options: ['0.5', '1.0', '2.0', '750'],
              correctIndex: 2,
              explanation:
                'Debt-to-Equity = Total Debt ($500M) ÷ Equity ($250M) = 2.0. This indicates high leverage.',
            },
            {
              id: 'l7-q2',
              question:
                'A company has $300M in current assets and $200M in current liabilities. What is its current ratio and what does it mean?',
              options: [
                '0.67: it can\'t cover its short-term bills',
                '1.5: it can comfortably cover short-term obligations',
                '500: it has plenty of cash',
                '100: it has a perfect ratio',
              ],
              correctIndex: 1,
              explanation:
                'Current Ratio = $300M ÷ $200M = 1.5. A ratio above 1.0 means the company has more current assets than current liabilities, so it can pay its near-term bills.',
            },
            {
              id: 'l7-q3',
              question: 'What is Free Cash Flow?',
              options: [
                'Total revenue minus total expenses',
                'Operating cash flow minus capital expenditures',
                'Cash in the bank',
                'Dividends paid to shareholders',
              ],
              correctIndex: 1,
              explanation:
                'Free Cash Flow = Operating Cash Flow - Capital Expenditures. It represents cash truly available after maintaining and expanding the business.',
            },
          ],
        },

        // ─── Level 8 ──────────────────────────────────────────
        {
          id: 'level-8',
          title: 'Putting It All Together',
          subtitle: 'Comparing companies using multiple metrics',
          screens: [
            {
              type: 'quick-calc',
              title: 'No Single Metric Tells the Whole Story',
              icon: '🧩',
              body: 'A high P/E could mean a company is overvalued, or that investors expect explosive growth. A low debt-to-equity could mean safety, or that the company isn\'t taking advantage of cheap debt. You need to combine metrics for the full picture.',
              scenario:
                'A company has a P/E of 5 and a debt-to-equity of 4.0. Is this a great bargain?',
              options: [
                'Yes, low P/E means cheap!',
                'Maybe not, the high debt makes it risky despite the low P/E',
                'P/E and D/E are unrelated',
                'Always buy low P/E stocks',
              ],
              correctIndex: 1,
              explanation:
                'A high P/E could mean overvalued or high expectations. A low D/E could mean safety or underutilized capital. You need to combine metrics for the full picture.',
            },
            {
              type: 'sort-order',
              title: 'A Quick Analysis Framework',
              icon: '📋',
              body: 'When analyzing a company, follow a structured approach. Start with size, then check valuation, profitability, growth, and financial health. This framework gives you a complete picture every time.',
              prompt: 'Put these analysis steps in a logical order:',
              items: [
                '1. How big is it? → Market Cap',
                '2. Is it expensive? → P/E and P/S ratios',
                '3. Is it profitable? → Margins',
                '4. Is it growing? → Revenue & EPS Growth',
                '5. Is it financially healthy? → D/E, Current Ratio, FCF',
              ],
            },
            {
              type: 'classify',
              title: 'Company Showdown',
              icon: '🥊',
              body: 'Growth stocks and value stocks have very different metric profiles. Learning to recognize these patterns helps you quickly identify what kind of company you\'re looking at in Wallstreetle.',
              bucketA: 'Growth Stock',
              bucketB: 'Value Stock',
              items: [
                { text: 'P/E: 45', bucket: 'A' },
                { text: 'P/E: 14', bucket: 'B' },
                { text: 'Revenue Growth: 35%', bucket: 'A' },
                { text: 'Revenue Growth: 5%', bucket: 'B' },
                { text: 'Net Margin: 12%', bucket: 'A' },
                { text: 'Net Margin: 22%', bucket: 'B' },
              ],
            },
            {
              type: 'fill-in-the-blank',
              title: 'Context Is Everything',
              icon: '🌍',
              body: 'Consider two companies: TechCo trades at a P/E of 45 but is expanding rapidly with minimal debt. ValueCo trades at a P/E of 14 with stronger profit margins but slower expansion. Neither is objectively "better." They represent two different investing philosophies, and understanding the trade-offs helps you identify which type of company you\u2019re looking at in Wallstreetle.',
              template:
                'A growth stock typically has a {{0}} P/E and {{1}} revenue growth. A value stock typically has a {{2}} P/E and {{3}} margins.',
              answers: ['high', 'fast', 'low', 'stable'],
              distractors: ['negative', 'zero', 'infinite'],
            },
            {
              type: 'match-pairs',
              title: 'The Wallstreetle Edge',
              icon: '🎮',
              body: 'Every Wallstreetle puzzle gives you real financial metrics for a mystery company. Now that you know what each metric means, you can narrow down guesses faster. You\'ve got this!',
              pairs: [
                ['High P/E + high growth', 'Probably a tech company'],
                ['Low margins + massive revenue', 'Likely retail or grocery'],
                ['High D/E + energy sector', 'Capital-intensive industry'],
                ['80% gross margin', 'Software company'],
              ],
            },
            {
              type: 'tap-to-reveal-takeaways',
              title: 'Key Takeaways',
              icon: '🎯',
              bullets: [
                'Always look at multiple metrics. No single number tells the full story.',
                'Compare companies within the same industry for meaningful insights.',
                'Growth companies will have high valuations; value companies will have low ones. Neither is inherently better.',
                'You now have the framework to decode every Wallstreetle clue!',
              ],
            },
          ],
          quiz: [
            {
              id: 'l8-q1',
              question:
                'Company A: P/E 50, Revenue Growth 40%, Net Margin 8%. Company B: P/E 12, Revenue Growth 3%, Net Margin 25%. Which is more likely a growth stock?',
              options: ['Company A', 'Company B', 'Both equally', 'Neither'],
              correctIndex: 0,
              explanation:
                'Company A has a high P/E (investors paying a premium), high revenue growth (40%), and lower margins typical of reinvesting in growth. Classic growth stock profile.',
            },
            {
              id: 'l8-q2',
              question:
                'You see a mystery company with 80% gross margin, high P/E, and strong revenue growth. Which sector is it most likely from?',
              options: [
                'Grocery retail',
                'Oil & gas',
                'Software/Technology',
                'Manufacturing',
              ],
              correctIndex: 2,
              explanation:
                'Software companies typically have very high gross margins (70-85%) because digital products cost little to reproduce. Combined with high P/E and growth, this screams tech.',
            },
            {
              id: 'l8-q3',
              question:
                'Which combination of metrics would concern you the most?',
              options: [
                'High P/E + High Revenue Growth',
                'Low Margins + High Revenue (retail model)',
                'Negative Free Cash Flow + High Debt-to-Equity + Declining Revenue',
                'Low P/E + Moderate Growth',
              ],
              correctIndex: 2,
              explanation:
                'Negative free cash flow + high debt + declining revenue is a dangerous combination. The company is burning cash, heavily leveraged, and shrinking.',
            },
            {
              id: 'l8-q4',
              question:
                'What\'s the first step in analyzing any company\'s financial data?',
              options: [
                'Buy the stock immediately',
                'Check if the P/E is above 20',
                'Look at multiple metrics and compare within the industry',
                'Focus only on revenue',
              ],
              correctIndex: 2,
              explanation:
                'No single metric is enough. A thorough analysis considers valuation, profitability, growth, and financial health, always compared to industry peers.',
            },
          ],
        },
      ],
    },
  ],

  // ═══════════════════════════════════════════════════════════
  // FINAL QUIZ - Mixed questions from all levels
  // ═══════════════════════════════════════════════════════════
  finalQuiz: [
    {
      id: 'fq-1',
      question:
        'Which financial statement shows a company\'s assets, liabilities, and equity?',
      options: [
        'Income Statement',
        'Balance Sheet',
        'Cash Flow Statement',
        'Earnings Report',
      ],
      correctIndex: 1,
      explanation:
        'The Balance Sheet is a snapshot of what a company owns (assets), owes (liabilities), and the remainder for shareholders (equity).',
    },
    {
      id: 'fq-2',
      question:
        'A company has a P/E of 8 and its industry average is 22. What might this suggest?',
      options: [
        'The company is overvalued',
        'The company may be undervalued or has concerns the market is pricing in',
        'The P/E is meaningless without revenue',
        'The company has no earnings',
      ],
      correctIndex: 1,
      explanation:
        'A P/E significantly below the industry average could signal an undervalued stock, or that the market sees risks (slowing growth, management issues, etc.).',
    },
    {
      id: 'fq-3',
      question: 'What does a current ratio of 0.6 indicate?',
      options: [
        'The company has more current assets than liabilities',
        'The company has 60% profit margins',
        'The company may struggle to pay short-term obligations',
        'The company is growing at 60%',
      ],
      correctIndex: 2,
      explanation:
        'A current ratio below 1.0 means current liabilities exceed current assets, so the company may have difficulty paying near-term bills.',
    },
    {
      id: 'fq-4',
      question: 'Revenue minus COGS equals what?',
      options: ['Net Income', 'Operating Income', 'Gross Profit', 'Free Cash Flow'],
      correctIndex: 2,
      explanation:
        'Revenue - COGS = Gross Profit. It shows the profit from production before operating expenses, interest, and taxes.',
    },
    {
      id: 'fq-5',
      question:
        'A company has $1B in revenue, $200M net income, and $800M operating cash flow. What is its net margin?',
      options: ['80%', '20%', '12.5%', '25%'],
      correctIndex: 1,
      explanation:
        'Net Margin = Net Income ($200M) ÷ Revenue ($1B) = 20%. Operating cash flow is used for a different metric.',
    },
    {
      id: 'fq-6',
      question:
        'Which cash flow type is most important for assessing core business health?',
      options: ['Investing', 'Financing', 'Operating', 'Total'],
      correctIndex: 2,
      explanation:
        'Operating cash flow shows cash generated from the core business, the most direct measure of sustainable business health.',
    },
    {
      id: 'fq-7',
      question:
        'Company X: Revenue Growth 5%, P/E 12, Net Margin 20%, D/E 0.4. Company Y: Revenue Growth 40%, P/E 55, Net Margin 5%, D/E 0.1. Which is the value stock?',
      options: ['Company X', 'Company Y', 'Both', 'Neither'],
      correctIndex: 0,
      explanation:
        'Company X has slow growth, low P/E, high margins, and moderate debt, a classic value stock. Company Y is a growth stock with high P/E and fast growth.',
    },
    {
      id: 'fq-8',
      question:
        'If assets are $500M and liabilities are $300M, what is shareholders\' equity?',
      options: ['$800M', '$300M', '$200M', '$500M'],
      correctIndex: 2,
      explanation: 'Equity = Assets ($500M) - Liabilities ($300M) = $200M.',
    },
    {
      id: 'fq-9',
      question:
        'A company\'s gross margin is 75%. In which industry is it most likely operating?',
      options: ['Airline', 'Grocery', 'Software', 'Automotive'],
      correctIndex: 2,
      explanation:
        'Software companies typically have 70-85% gross margins due to minimal cost of reproducing digital products. Other industries listed have much lower gross margins.',
    },
    {
      id: 'fq-10',
      question:
        'Why is it important to compare metrics within the same industry?',
      options: [
        'Different industries have different norms for P/E, margins, and growth',
        'It\'s not important, metrics are universal',
        'Only revenue matters across industries',
        'The SEC requires it',
      ],
      correctIndex: 0,
      explanation:
        'Industries have vastly different cost structures, growth rates, and capital requirements. A 5% net margin is excellent for a grocery chain but poor for a software company.',
    },
  ],
};

// ─── Helper: Get all level IDs in order ───────────────────────
export function getAllLevelIds(course: Course): string[] {
  return course.sections.flatMap((s) => s.levels.map((l) => l.id));
}

// ─── Helper: Look up a level by ID ───────────────────────────
export function findLevelById(
  course: Course,
  levelId: string
): { section: typeof course.sections[number]; level: typeof course.sections[number]['levels'][number] } | null {
  for (const section of course.sections) {
    const level = section.levels.find((l) => l.id === levelId);
    if (level) return { section, level };
  }
  return null;
}

/** Final quiz level ID */
export const FINAL_QUIZ_ID = 'final-quiz';
